import type { GridGeometry } from '../grid';

interface TilePoint {
  x: number;
  y: number;
}

/**
 * Rotation helpers.
 * Convention: 0° = North, 90° = East, clockwise positive.
 */
export function normalizeRotation(degrees: number): number {
  return ((degrees % 360) + 360) % 360;
}

export function calculateAngleDifference(currentRotation: number, targetRotation: number): number {
  let diff = normalizeRotation(targetRotation) - normalizeRotation(currentRotation);
  while (diff > 180) diff -= 360;
  while (diff < -180) diff += 360;
  return diff;
}

/**
 * Compute facing angle from world-space delta.
 */
export function calculateTargetAngleFromDelta(dx: number, dy: number): number {
  return normalizeRotation(Math.atan2(dx, -dy) * (180 / Math.PI));
}

/**
 * Compute facing from tile-center to tile-center for the provided grid geometry.
 */
export function calculateTargetAngleFromTileCenters(
  grid: GridGeometry,
  from: TilePoint,
  to: TilePoint,
  tileSize = 1,
): number {
  const fromPixel = grid.tileToPixel(from.x, from.y, tileSize);
  const toPixel = grid.tileToPixel(to.x, to.y, tileSize);
  const fromCentroid = grid.tileCentroid(tileSize, from.x, from.y);
  const toCentroid = grid.tileCentroid(tileSize, to.x, to.y);

  const fromCenterX = fromPixel.x + fromCentroid.x;
  const fromCenterY = fromPixel.y + fromCentroid.y;
  const toCenterX = toPixel.x + toCentroid.x;
  const toCenterY = toPixel.y + toCentroid.y;

  const dx = toCenterX - fromCenterX;
  const dy = toCenterY - fromCenterY;
  return calculateTargetAngleFromDelta(dx, dy);
}

export function rotationToStorageInteger(rotation: number): number {
  return Math.round(normalizeRotation(rotation));
}
